import React, { FC, useRef } from 'react';
import Svg from 'svg-inline-react';
import classNames from 'classnames';
import EmojiPicker from '@src/components/EmojiPicker';
import { parseEmojis } from '@src/util/parseEmojis';
import { Button } from '@src/components/Button';
import FrCheckSvg from '@src/svgs/fr-check.svg';
import FrCloseSvg from '@src/svgs/fr-close.svg';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { MessageThreadStateEnum, RecruiterApplicationStateEnum, useSessionQuery } from '@src/graphql/generated';
import { Appearance } from '@src/components/Button/enums/appearance.enum';
import { trackEvent } from '@src/metrics';
import { ButtonProps } from './interfaces/buttonProps.interface';
import { MessageEntryAreaWithQuickActionsProps } from './props';
import '../styles.scss';

const TEXT_AREA_HEIGHT = 2.4;
const CHAT_CLOSED_MESSAGE = 'This chat has been closed';

export const MessageEntryAreaWithQuickActions: FC<MessageEntryAreaWithQuickActionsProps> = ({
  message,
  metricsContext,
  onChange,
  onSend,
  onReject,
  onAccept,
  isDisabled,
  onClose,
  onReopen,
  messageThread,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const { data: sessionData } = useSessionQuery();

  const isSiteAdmin = sessionData?.currentRecruiter?.isSiteAdmin;

  if (!messageThread) {
    return <div className="message-entry-area" />;
  }

  const threadId = messageThread?.id;
  const recruiterState = messageThread?.application?.recruiterState?.state;
  const candidateChatState = messageThread?.userState;
  const applicationId = messageThread?.application?.id;
  const recruiterChatState = messageThread?.recruiterState;
  const openingId = messageThread?.opening?.id;

  const isChatClosedByCandidate = candidateChatState === MessageThreadStateEnum.CLOSED;
  const isChatClosedByRecruiter = recruiterChatState === MessageThreadStateEnum.CLOSED;
  const isPending = recruiterState === RecruiterApplicationStateEnum.PENDING;
  const isChatClosed = isChatClosedByCandidate || isChatClosedByRecruiter;

  const handleSend = (): void => {
    onSend();

    trackEvent({
      context: MetricContext.CHAT,
      interaction: MetricAction.SEND_CHAT_MESSAGE,
      payload: {
        'Application Id': applicationId,
        'Event Source': MetricEventSource.SEND_BUTTON_IN_INPUT_FIELD,
        'Opening Id': openingId,
        'Previous Context': metricsContext,
        'Thread Id': threadId,
      },
    });
  };

  const handleReject = (): void => {
    onReject();

    trackEvent({
      context: MetricContext.CANDIDATE_MANAGEMENT,
      interaction: MetricAction.RESPOND_TO_CANDIDATE,
      payload: {
        'Application Id': applicationId,
        'Event Source': MetricEventSource.REJECT_BUTTON,
        'Opening Id': openingId,
        'Previous Context': metricsContext,
        'Type': 'Reject',
      },
    });
  };

  const handleAccept = (): void => {
    onAccept();

    trackEvent({
      context: MetricContext.CANDIDATE_MANAGEMENT,
      interaction: MetricAction.RESPOND_TO_CANDIDATE,
      payload: {
        'Application Id': applicationId,
        'Event Source': MetricEventSource.REPLY_BUTTON,
        'Opening Id': openingId,
        'Previous Context': metricsContext,
        'Type': 'Reply',
      },
    });
  };

  const handleClose = (): void => {
    if (onClose) {
      onClose();

      trackEvent({
        context: MetricContext.END_CHAT,
        interaction: MetricAction.END_CHAT,
        payload: {
          'Application Id': applicationId,
          'Opening Id': openingId,
          'Thread Id': threadId,
          'Type': 'Close',
        },
      });
    }
  };

  const handleReopen = (): void => {
    if (onReopen) {
      onReopen();

      trackEvent({
        context: MetricContext.CHAT,
        interaction: MetricAction.REOPEN_CHAT,
        payload: {
          'Application Id': applicationId,
          'Event Source': MetricEventSource.REOPEN_BUTTON_IN_INPUT_FIELD,
          'Opening Id': openingId,
          'Previous Context': metricsContext,
          'Thread Id': threadId,
        },
      });
    }
  };

  const clickHandler = (action: () => void) => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = TEXT_AREA_HEIGHT + 'rem';
    }

    action();
  };

  const BUTTON_TYPES: Record<string, ButtonProps[]> = {
    [RecruiterApplicationStateEnum.ACCEPTED]: [
      {
        disabled: isDisabled,
        handleClick: () => clickHandler(handleSend),
        label: 'Send',
      },
    ],
    [RecruiterApplicationStateEnum.PENDING]: [
      {
        appearance: Appearance.RED,
        disabled: false,
        handleClick: () => clickHandler(handleReject),
        icon: FrCloseSvg,
        label: 'No',
      },
      {
        appearance: Appearance.SP3,
        disabled: false,
        handleClick: () => clickHandler(handleAccept),
        icon: FrCheckSvg,
        label: 'Yes',
      },
    ],
    [RecruiterApplicationStateEnum.REJECTED]: [
      {
        appearance: Appearance.SP3,
        disabled: isDisabled,
        handleClick: () => clickHandler(handleAccept),
        label: 'Yes',
      },
    ],
    closedByCandidate: [
      {
        disabled: false,
        handleClick: () => clickHandler(handleClose),
        label: 'Close',
      },
    ],
    closedByRecruiter: [
      {
        disabled: false,
        handleClick: () => clickHandler(handleReopen),
        label: 'Reopen',
      },
    ],
  };

  const onChangeHandler = (updatedValue: string): void => {
    const parsedVal = parseEmojis(updatedValue);

    if (textAreaRef.current) {
      textAreaRef.current.style.height = TEXT_AREA_HEIGHT + 'rem';
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight / 10 + 'rem';
    }

    onChange(parsedVal);
  };

  const isMultiline = message.split(/\r*\n/).length > 1;

  let buttonsToShow = recruiterState ? BUTTON_TYPES[recruiterState] : [];
  let closedMessage = '';

  if (isChatClosedByCandidate) {
    buttonsToShow = onClose ? BUTTON_TYPES.closedByCandidate : [];
    closedMessage = 'This chat has been closed by applicant.';
  }

  if (isChatClosedByRecruiter) {
    buttonsToShow = onReopen ? BUTTON_TYPES.closedByRecruiter : [];
    closedMessage = CHAT_CLOSED_MESSAGE;
  }

  // hide buttons if chat is closed from both sides
  if (isChatClosedByCandidate && isChatClosedByRecruiter) {
    buttonsToShow = [];
    closedMessage = CHAT_CLOSED_MESSAGE;
  }

  if (isSiteAdmin) {
    buttonsToShow = BUTTON_TYPES[RecruiterApplicationStateEnum.ACCEPTED];
  }

  const getInputValue = (): string => {
    if (isSiteAdmin) {
      return message;
    }

    if (isChatClosed) {
      return closedMessage;
    }

    if (isPending) {
      return 'Include in the hiring pipeline?';
    }

    return message;
  };

  const getPlaceholder = (): string => {
    if (isChatClosed) {
      return closedMessage;
    }

    if (isPending) {
      return 'Include in the hiring pipeline?';
    }

    return 'Type message';
  };

  const isChatDisabled = !isSiteAdmin && (isChatClosed || isPending);

  return (
    <div className="message-entry-area">
      {!isChatDisabled && (
        <div className="message-entry-area__icon-container">
          <EmojiPicker onSelect={emoji => onChangeHandler(message + emoji)} />
        </div>
      )}

      <div className="message-entry-area__text-container">
        <textarea
          className={classNames(
            'message-entry-area__textarea',
            isMultiline && 'message-entry-area__textarea--multiline',
          )}
          disabled={isChatDisabled}
          onChange={e => onChangeHandler(e.target.value)}
          onKeyDown={e => {
            // send message on enter click
            if (e.keyCode === 13 && !e.shiftKey) {
              e.preventDefault();

              if (isDisabled || recruiterState === RecruiterApplicationStateEnum.PENDING) {
                return;
              }

              const handleClick = recruiterState ? BUTTON_TYPES[recruiterState][0].handleClick : undefined;

              handleClick?.();
            }
          }}
          placeholder={getPlaceholder()}
          ref={textAreaRef}
          style={{ height: TEXT_AREA_HEIGHT + 'rem' }}
          value={getInputValue()}
        />
      </div>

      <div className="message-entry-area__button-container">
        {buttonsToShow.map((buttonProps, index) => (
          <Button
            appearance={buttonProps.appearance}
            className={classNames(
              'message-entry-area__button',
              buttonProps.icon && 'message-entry-area__button--with-icon',
            )}
            isDisabled={buttonProps.disabled}
            key={index}
            onClick={buttonProps.handleClick}
          >
            {buttonProps.icon && <Svg className="message-entry-area__icon" src={buttonProps.icon} />}

            {buttonProps.label}
          </Button>
        ))}
      </div>
    </div>
  );
};
